import type { SwipeEventData } from "react-swipeable";

/**
 * Elements that own their horizontal drags: form inputs / ARIA sliders (incl.
 * the viewer's custom video scrub slider) and anything opted out via
 * `data-no-swipe` (the viewer's bottom toolbar + scrub strip). A drag starting
 * on these must never be treated as carousel navigation — scrubbing a video's
 * timeline was flipping to the next item mid-scrub.
 *
 * `video` itself is deliberately NOT here (it was, while videos carried native
 * controls whose shadow-DOM drags retarget to the <video> host): the viewer's
 * videos are bare surfaces now — playback controls live in the toolbar/scrub
 * strip — so a horizontal swipe on the video navigates, same as on an image.
 */
export const NO_SWIPE_SELECTOR = "input, [role='slider'], [data-no-swipe]";

/**
 * True when the gesture STARTED on an interactive element. Touch events keep
 * their touchstart target for the entire gesture, so this is stable however
 * far the finger travels.
 */
export function startedOnInteractive(eventData: SwipeEventData): boolean {
  const target = eventData.event.target;
  return target instanceof Element && !!target.closest(NO_SWIPE_SELECTOR);
}

/**
 * True for a decisive horizontal swipe. react-swipeable's `delta` only gates
 * when TRACKING starts — the onSwiped{Left,Right} callbacks fire at touchend
 * with the NET direction, however small the net displacement. So a drag pulled
 * back to its origin (a "cancel" in every native gallery) or a mostly-vertical
 * drag must not navigate.
 */
export function isDecisiveHorizontalSwipe(
  eventData: SwipeEventData,
  minDelta = 50,
): boolean {
  return eventData.absX >= minDelta && eventData.absX > eventData.absY;
}

/**
 * True for a decisive VERTICAL swipe (mirror of the horizontal guard) — used
 * for swipe-down-to-dismiss, so a mostly-horizontal drag or a tiny net vertical
 * displacement doesn't close the viewer. Direction (up vs down) is decided by
 * the caller (react-swipeable's onSwipedDown already implies downward).
 */
export function isDecisiveVerticalSwipe(
  eventData: SwipeEventData,
  minDelta = 50,
): boolean {
  return eventData.absY >= minDelta && eventData.absY > eventData.absX;
}
